import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';


/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const BackgroundVideo = (props) => {
    // state
    const [videoHeight, setVideoHeight] = useState(undefined);
    const [videoWidth, setVideoWidth] = useState(undefined);

    // refs
    const containerRef = useRef();
    const videoRef = props.videoRef;

    // methods
    const handleResize = () => {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;

        const videoAspectRatio = videoRef.current.videoWidth / videoRef.current.videoHeight;

        const _videoWidth = containerHeight * videoAspectRatio;
        const _videoHeight = containerWidth * (1 / videoAspectRatio);

        let height = '100%';
        let width = '100%';

        if (containerWidth > _videoWidth) {
            height = `${(_videoHeight / containerHeight) * 100}%`;
            width = '100%';
        } else {
            height = '100%';
            width = `${(_videoWidth / containerWidth) * 100}%`;
        }

        setVideoHeight(height);
        setVideoWidth(width);
    };

    // hooks
    useEffect(() => {
        const video = videoRef?.current;

        if (video) {
            window.addEventListener('resize', handleResize, { passive: true });
            video.addEventListener('loadedmetadata', handleResize, { passive: true });

            window.setTimeout(() => {
                handleResize();
            }, 100);
        }

        // component unmount
        return () => {
            if (video) {
                window.removeEventListener('resize', handleResize, { passive: true });
                video.removeEventListener('loadedmetadata', handleResize, { passive: true });
            }
        };
    }, []); // run on mount only

    return (
        <div
            className={props.containerClassName ? props.containerClassName : 'absolute top-0 right-0 bottom-0 left-0'}
            ref={containerRef}>
            <video
                autoPlay
                disablePictureInPicture
                loop
                muted
                playsInline
                className={props.className ? props.className : 'absolute h-full w-full'}
                ref={videoRef}
                src={props.src}
                style={{ height: videoHeight, width: videoWidth }} />

            <div className='absolute top-0 right-0 bottom-0 left-0' />
        </div>
    );
};


/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    src: PropTypes.string.isRequired,
    videoRef: PropTypes.object
};

BackgroundVideo.propTypes = propTypes;

export default BackgroundVideo;
